import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  useNavigate,
  Link,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import LogoutModal from "./ui/LogoutModal";
import HostSettingsModal from "./ui/HostSettingsModal";
import {
  Activity,
  LayoutDashboard,
  List,
  LogOut,
  Search,
  Bell,
  BarChart3,
  Globe,
  FileText,
  MessageSquare,
  Menu,
  X,
  Key,
  Settings,
  Smartphone,
} from "lucide-react";

const theme = {
  primary: "#7c3aed",
  primaryLight: "#ede9fe",
  bg: "#f5f3ff",
  surface: "#ffffff",
  border: "#e9e5f5",
  text: { primary: "#1e1b4b", secondary: "#6b7280", muted: "#9ca3b8" },
};

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [globalSearch, setGlobalSearch] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hostModalOpen, setHostModalOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => setLogoutModalOpen(true);

  const handleConfirmLogout = () => {
    setLogoutModalOpen(false);
    logout();
    navigate("/login");
  };

  const handleGlobalSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && globalSearch.trim()) {
      navigate(`/logs?q=${encodeURIComponent(globalSearch.trim())}`);
    }
  };

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  const mainNavItems = [
    { path: "/", label: "Dashboard", icon: LayoutDashboard },
    { path: "/logs", label: "Explorateur", icon: List },
    { path: "/stats", label: "Statistiques", icon: BarChart3 },
    { path: "/platforms", label: "Plateformes", icon: Smartphone },
  ];

  const secondaryNavItems = [
    { path: "/services", label: "Services", icon: Globe },
    { path: "/reports", label: "Rapports", icon: FileText },
    { path: "/apikeys", label: "Clés API", icon: Key },
  ];

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: theme.bg }}>
      {/* ── Mobile overlay ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {/* ── Right area (header + content) ── */}
      <div className="flex flex-col min-h-screen flex-1 min-w-0">
        {/* Top bar */}
        <header
          className="fixed top-0 left-0 right-0 z-30 h-14 flex items-center justify-between px-4 md:px-7 gap-4"
          style={{
            backgroundColor: theme.surface,
            borderBottom: `1px solid ${theme.border}`,
          }}
        >
          {/* Left: hamburger (mobile) + logo + search */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
              style={{ border: "none", background: "transparent" }}
            >
              <Menu
                className="w-5 h-5"
                style={{ color: theme.text.secondary }}
              />
            </button>

            {/* Logo */}
            <div className="flex items-center gap-2.5 flex-shrink-0">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: theme.primary }}
              >
                <Activity className="w-4 h-4 text-white" />
              </div>
              <span
                className="font-bold text-[15px]"
                style={{ color: theme.text.primary }}
              >
                API Monitor
              </span>
            </div>

            {/* <div className="relative h-9 flex items-center flex-1 max-w-xs">
              <Search
                className="absolute left-3 w-4 h-4 pointer-events-none"
                style={{ color: theme.text.muted }}
              />
              <input
                type="text"
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                onKeyDown={handleGlobalSearch}
                placeholder="Rechercher..."
                className="w-full h-full pl-9 pr-3 rounded-full text-[13px]"
                style={{
                  backgroundColor: '#faf9fc',
                  border: `1px solid ${theme.border}`,
                  color: theme.text.primary,
                  outline: 'none',
                  lineHeight: '1.5',
                }}
              />
            </div> */}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-3 md:gap-5 flex-shrink-0">
            <button
              className="relative p-1.5 rounded-full hover:bg-gray-100 transition-colors hidden sm:flex"
              style={{ background: "transparent", border: "none" }}
            >
              <MessageSquare
                className="w-[18px] h-[18px]"
                style={{ color: theme.text.secondary }}
              />
            </button>
            <button
              className="relative p-1.5 rounded-full hover:bg-gray-100 transition-colors hidden sm:flex"
              style={{ background: "transparent", border: "none" }}
            >
              <Bell
                className="w-[18px] h-[18px]"
                style={{ color: theme.text.secondary }}
              />
            </button>
            {mode == "debug" && (
              <button
                onClick={() => setHostModalOpen(true)}
                title="Changer le serveur API"
                className="relative p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Settings
                  className="w-[18px] h-[18px]"
                  style={{ color: theme.text.secondary }}
                />
              </button>
            )}

            {/* User avatar */}
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white cursor-pointer flex-shrink-0"
              style={{ backgroundColor: theme.primary }}
              title={user?.name}
            >
              {user?.name?.charAt(0) || "U"}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 pt-20 px-4 md:px-7 pb-4 md:pb-7 flex gap-6">
          {/* Fixed sidebar menu */}
          <aside
            className="hidden md:flex flex-col w-56 flex-shrink-0 fixed left-0 top-0 h-screen z-20 pt-20"
            style={{
              backgroundColor: theme.surface,
              borderRight: `1px solid ${theme.border}`,
            }}
          >
            <nav className="flex-1 px-3 pt-6 space-y-1">
              {mainNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-base font-medium transition-colors"
                    style={{
                      backgroundColor: isActive
                        ? theme.primaryLight
                        : "transparent",
                      color: isActive ? theme.primary : theme.text.secondary,
                    }}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}

              <div
                className="pt-4 mt-4"
                style={{ borderTop: `1px solid ${theme.border}` }}
              />

              {secondaryNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-base font-medium transition-colors"
                    style={{
                      backgroundColor: isActive
                        ? theme.primaryLight
                        : "transparent",
                      color: isActive ? theme.primary : theme.text.secondary,
                    }}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Logout button at bottom */}
            <div className="px-3 pb-6">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-base font-medium w-full text-left transition-colors hover:bg-red-50"
                style={{
                  color: "#ef4444",
                  background: "transparent",
                  border: "none",
                }}
              >
                <LogOut className="w-5 h-5" />
                <span>Déconnexion</span>
              </button>
            </div>
          </aside>

          {/* Main content with left margin to account for fixed sidebar */}
          <div className="flex-1 min-w-0 md:ml-56">{children}</div>
        </main>
      </div>

      <HostSettingsModal
        open={hostModalOpen}
        onClose={() => setHostModalOpen(false)}
      />
      <LogoutModal
        open={logoutModalOpen}
        onConfirm={handleConfirmLogout}
        onCancel={() => setLogoutModalOpen(false)}
      />
    </div>
  );
}
